import React, { useEffect, useState, useRef, useMemo } from 'react'; // Added useRef, useMemo
import { QRCodeSVG } from 'qrcode.react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import './ManajemenInstrumen.css';

// Updated initialForm to use Base64 fields
const initialForm = { nama: '', kode: '', spesifikasi: '', lokasi: '', gambarBase64: '', sopBase64: '', sopFileName: '' };

const ManajemenInstrumen = () => {
  const [instrumen, setInstrumen] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState(''); // State for search term
  const [bulkAddStatus, setBulkAddStatus] = useState(''); // State for bulk add feedback
  const [selectedImage, setSelectedImage] = useState(null); // State for image popup
  const navigate = useNavigate(); // Hook for navigation

  // Data to be added
  const instrumentsToAdd = [
    { nama: 'Autoclave', kode: 'AC-001' },
    { nama: 'Colony Counter', kode: 'CC-001' },
    { nama: 'Furnace', kode: 'FR-001' },
    { nama: 'Hotplate Stirrer DLAB NS7H550 PRO', kode: 'HS-001' },
    { nama: 'Hotplate Stirrer SH HSD', kode: 'HS-002' },
    { nama: 'Inkubator', kode: 'IN-001' },
    { nama: 'Laminar Air Flow', kode: 'LAF-001' },
    { nama: 'Lemari Asam (Fume Hood)', kode: 'FH-001' },
    { nama: 'Mikroskop Optik', kode: 'MO-001' },
    { nama: 'Oven Labtech', kode: 'OV-001' },
    { nama: 'Oven Memmert', kode: 'OV-002' },
    { nama: 'pH Meter Hanna', kode: 'PM-001' },
    { nama: 'Rotary Evaporator', kode: 'RE-001' },
    { nama: 'Sentrifugasi', kode: 'SF-001' },
    { nama: 'Spektrofotometer UV', kode: 'SP-001' },
    { nama: 'Timbangan Analitik', kode: 'TA-001' },
    { nama: 'Ultra Turrax', kode: 'UT-001' },
    { nama: 'Ultrasonic WB Ovan', kode: 'US-001' },
    { nama: 'Vortex', kode: 'VX-001' },
    { nama: 'Waterbath', kode: 'WB-001' }
  ];

  // Fetch data
  const fetchInstrumen = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'instrumen'));
      const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setInstrumen(data);
    } catch (err) {
      setError('Gagal mengambil data instrumen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInstrumen(); }, []);

  // Filtered list based on search term
  const filteredInstrumen = useMemo(() => {
    if (!searchTerm) {
      return instrumen;
    }
    return instrumen.filter(item =>
      item.nama.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.kode && item.kode.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [instrumen, searchTerm]);

  // Handle form input
  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Helper function to read file as Base64
  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result); // result includes 'data:mime/type;base64,' prefix
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  };


  // Handle file input - Convert to Base64 and store in form state
  const handleGambarChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1048576) { // Check Firestore document size limit (1 MiB)
         setError('Ukuran file gambar terlalu besar (maks 1MB).');
         e.target.value = null; // Clear the input
         return;
      }
      try {
        const base64 = await readFileAsBase64(file);
        setForm({ ...form, gambarBase64: base64 });
        setError(''); // Clear previous error
      } catch (err) {
        console.error("Error reading image file:", err);
        setError('Gagal membaca file gambar.');
        setForm({ ...form, gambarBase64: '' }); // Clear on error
      }
    } else {
       setForm({ ...form, gambarBase64: '' }); // Clear if no file selected
    }
  };

  const handleSOPChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
       if (file.size > 1048576) { // Check Firestore document size limit (1 MiB)
         setError('Ukuran file SOP terlalu besar (maks 1MB).');
         e.target.value = null; // Clear the input
         return;
       }
      try {
        const base64 = await readFileAsBase64(file);
        // Store filename separately for potential download functionality later
        setForm({ ...form, sopBase64: base64, sopFileName: file.name });
         setError(''); // Clear previous error
      } catch (err) {
        console.error("Error reading SOP file:", err);
        setError('Gagal membaca file SOP.');
        setForm({ ...form, sopBase64: '', sopFileName: '' }); // Clear on error
      }
    } else {
       setForm({ ...form, sopBase64: '', sopFileName: '' }); // Clear if no file selected
    }
  };

  // Add or update - Save Base64 strings to Firestore
  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    let success = false; // Flag to track success

    // Ensure Base64 data is included directly from the form state
    const dataToSave = {
      nama: form.nama,
      kode: form.kode,
      spesifikasi: form.spesifikasi,
      lokasi: form.lokasi,
      gambarBase64: form.gambarBase64 || '', // Use Base64 field
      sopBase64: form.sopBase64 || '',       // Use Base64 field
      sopFileName: form.sopFileName || ''    // Store filename too
    };

    // Validate required fields
    if (!dataToSave.nama || !dataToSave.kode) {
        setError('Nama Instrumen and Kode Unik are required.');
        setLoading(false);
        return;
    }


    try {
      // No need for separate upload logic, dataToSave contains Base64 strings

      if (editId) {
        // When updating, ensure we don't accidentally overwrite with empty strings
        // if the user didn't select new files. The current form state already holds
        // either the old Base64 or the new one if selected.
        await updateDoc(doc(db, 'instrumen', editId), dataToSave);
        console.log("Instrument updated successfully:", editId);
      } else {
        const docRef = await addDoc(collection(db, 'instrumen'), dataToSave);
        console.log("Instrument added successfully:", docRef.id);
      }

      success = true; // Mark as successful

    } catch (err) {
      console.error("Error saving instrument:", err);
      setError(`Gagal menyimpan data: ${err.message || 'Unknown error'}`);
      // Keep form data for correction, don't reset on error
    } finally {
      setLoading(false); // Always stop loading indicator
      if (success) {
        // Reset form only on successful save
        setForm(initialForm); // Reset form
        setEditId(null);
        // Clear file inputs visually (though state is handled by form reset)
        document.querySelectorAll('input[type="file"]').forEach(input => input.value = '');
        fetchInstrumen(); // Refresh the list
      }
    }
  };

  // Edit - Load Base64 data into form
  const handleEdit = item => {
    setForm({
      nama: item.nama || '',
      kode: item.kode || '',
      spesifikasi: item.spesifikasi || '',
      lokasi: item.lokasi || '',
      gambarBase64: item.gambarBase64 || '', // Load Base64
      sopBase64: item.sopBase64 || '',       // Load Base64
      sopFileName: item.sopFileName || ''    // Load filename
    });
    setEditId(item.id);
    // Clear file inputs visually when starting an edit
    document.querySelectorAll('input[type="file"]').forEach(input => input.value = '');
    setError(''); // Clear error when starting edit
    setBulkAddStatus(''); // Clear bulk add status
  };

  // Delete
  const handleDelete = async id => {
    if (!window.confirm('Hapus instrumen ini?')) return;
    setLoading(true);
    setError(''); // Clear previous errors
    try {
      // No need to delete files from Storage, just delete the Firestore doc
      await deleteDoc(doc(db, 'instrumen', id));
      fetchInstrumen(); // Refresh list
    } catch (err) {
      console.error("Error deleting instrument:", err);
      // Keep error state consistent
      setError(`Gagal menghapus data: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false); // Ensure loading is stopped
    }
  };

  // Bulk Add Function
  const handleBulkAdd = async () => {
    setLoading(true);
    setError('');
    setBulkAddStatus('Menambahkan instrumen...');
    let addedCount = 0;
    let skippedCount = 0;
    let errorCount = 0;

    const instrumenCollection = collection(db, 'instrumen');

    for (const instrument of instrumentsToAdd) {
      try {
        // Check if instrument with the same code already exists
        const q = query(instrumenCollection, where("kode", "==", instrument.kode));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          // Add instrument if it doesn't exist
          await addDoc(instrumenCollection, {
            ...initialForm, // Start with default empty fields
            nama: instrument.nama,
            kode: instrument.kode,
            // Add default values or leave others empty as needed
            spesifikasi: '',
            lokasi: '',
            gambarBase64: '',
            sopBase64: '',
            sopFileName: ''
          });
          addedCount++;
        } else {
          // Skip if instrument already exists
          skippedCount++;
          console.log(`Instrument with code ${instrument.kode} already exists. Skipping.`);
        }
      } catch (err) {
        console.error(`Error adding instrument ${instrument.kode}:`, err);
        setError(`Gagal menambahkan ${instrument.nama} (${instrument.kode}).`);
        errorCount++;
        // Optionally break the loop on first error or continue
        // break;
      }
    }

    setLoading(false);
    setBulkAddStatus(`Selesai: ${addedCount} ditambahkan, ${skippedCount} dilewati, ${errorCount} gagal.`);
    if (addedCount > 0 || skippedCount > 0) {
        fetchInstrumen(); // Refresh list if anything changed or was skipped (to show existing)
    }
    // Clear status after a few seconds
    setTimeout(() => setBulkAddStatus(''), 5000);
  };


  // Function to handle QR Code download
  const handleDownloadQR = (itemId, itemName) => {
    const svgElement = document.getElementById(`qr-${itemId}`);
    if (!svgElement) {
      console.error("QR Code SVG element not found for ID:", itemId);
      return;
    }

    const svgString = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      // --- Canvas Drawing Logic ---
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const targetQrSize = 256; // Define a larger size for the downloaded QR code
      const padding = 20;      // Padding around QR code and text
      const fontSize = 16;     // Font size for the name
      const lineHeight = fontSize * 1.2; // Line height for potential wrapping
      const textMaxWidth = targetQrSize; // Max width for the text block

      ctx.font = `${fontSize}px Arial`;
      ctx.textAlign = 'center';

      // Function to wrap text
      const wrapText = (context, text, x, y, maxWidth, lineHeight) => {
        const words = text.split(' ');
        let line = '';
        let lines = [];

        for(let n = 0; n < words.length; n++) {
          const testLine = line + words[n] + ' ';
          const metrics = context.measureText(testLine);
          const testWidth = metrics.width;
          if (testWidth > maxWidth && n > 0) {
            lines.push(line);
            line = words[n] + ' ';
          } else {
            line = testLine;
          }
        }
        lines.push(line);

        // Draw wrapped lines
        lines.forEach((line, index) => {
           context.fillText(line.trim(), x, y + (index * lineHeight));
        });
        return lines.length; // Return number of lines drawn
      };

      // Calculate required text height based on wrapping
      const textToDraw = itemName || 'Instrument';
      const numLines = wrapText(ctx, textToDraw, 0, 0, textMaxWidth, lineHeight); // Dry run to get line count
      const totalTextHeight = numLines * lineHeight;

      // Set final canvas dimensions
      canvas.width = targetQrSize + 2 * padding;
      canvas.height = targetQrSize + totalTextHeight + 3 * padding; // QR + Text + Padding top/middle/bottom

      // White background (redraw after setting final size)
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw QR code image onto canvas (larger size) with padding
      ctx.drawImage(img, padding, padding, targetQrSize, targetQrSize);

      // Add instrument name text below QR code (with wrapping)
      ctx.fillStyle = 'black'; // Set fillStyle again after potential context reset
      ctx.font = `${fontSize}px Arial`; // Set font again
      ctx.textAlign = 'center'; // Set textAlign again
      const textY = targetQrSize + 2 * padding; // Y position for the first line of text
      wrapText(ctx, textToDraw, canvas.width / 2, textY, textMaxWidth, lineHeight); // Actual drawing with wrapping

      // Trigger download
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      // Sanitize filename
      const safeItemName = (itemName || 'qrcode').replace(/[^a-z0-9]/gi, '_').toLowerCase();
      downloadLink.href = pngUrl;
      downloadLink.download = `QR_${safeItemName}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url); // Clean up blob URL
    };
    img.onerror = (err) => {
      console.error("Error loading SVG as image:", err);
      URL.revokeObjectURL(url); // Clean up blob URL on error
    };
    img.src = url;
  };

  // Handler for "Lihat Jadwal" button
  const handleLihatJadwal = (item) => {
    // Navigate to the Admin's report/schedule page
    navigate('/admin/laporan-penggunaan-alat', {
       state: { alatId: item.id, namaAlat: item.nama, type: 'instrumen' } // Pass necessary info
    });
  };

  // Handle SOP button click (similar to QRScanner)
  const handleBacaSOP = (item) => {
    if (!item) return;

    // Prefer URL if available - Opens in new tab
    if (item.sopUrl) {
      window.open(item.sopUrl, '_blank', 'noopener,noreferrer');
    }
    // Fallback to Base64 - Opens in new tab (browser handles PDF rendering)
    else if (item.sopBase64) {
      // Check if it's a PDF Base64 string
      if (item.sopBase64.startsWith('data:application/pdf;base64,')) {
        const pdfWindow = window.open("");
        pdfWindow.document.write(
          "<iframe width='100%' height='100%' src='" +
          item.sopBase64 +
          "'></iframe>"
        );
      } else {
        // For non-PDF Base64 (like DOCX), trigger download as fallback
        const link = document.createElement('a');
        link.href = item.sopBase64;
        link.download = item.sopFileName || 'SOP_File'; // Use filename or default
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } else {
      // Handle case where neither is available, maybe show an alert
      alert('File SOP tidak tersedia.');
    }
  };

  // Handler for image click
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  // Handler to close image popup
  const handleCloseImage = () => {
    setSelectedImage(null);
  };

  // Add states for popup management
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);

  // Modified handleEdit to show popup
  const handleEditClick = item => {
    handleEdit(item);
    setShowEditPopup(true);
  };

  // Handler for "Tambah Item" button
  const handleAddClick = () => {
    setForm(initialForm);
    setEditId(null);
    setError('');
    document.querySelectorAll('input[type="file"]').forEach(input => input.value = '');
    setShowAddPopup(true);
  };

  // Close popup handler
  const handleClosePopup = () => {
    if (editId) {
      setForm(initialForm);
      setEditId(null);
      setError('');
      document.querySelectorAll('input[type="file"]').forEach(input => input.value = '');
    }
    setShowAddPopup(false);
    setShowEditPopup(false);
  };

  return (
    <div className="manajemen-instrumen-container">
      <div className="header-section">
        <h2>Manajemen Instrumen</h2>
        <button 
          onClick={handleAddClick} 
          className="btn btn-primary btn-add-instrumen"
        >
          Tambah Instrumen
        </button>
      </div>

      {/* Display Error and Loading */}
      {error && <div className="error-message">{error}</div>}
      {bulkAddStatus && <div className="info-message">{bulkAddStatus}</div>} {/* Bulk Add Status */}
      {/* Keep loading message for initial load or delete action */}
      {loading && instrumen.length === 0 && !bulkAddStatus && <div className="loading-message">Memuat data...</div>} {/* Hide initial load if bulk add is running */}

      {/* Simple Search Input */}
      <div className="search-container">
        <input
          type="text"
          placeholder="Cari instrumen berdasarkan nama atau kode..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>


      {/* Add/Edit Popup */}
      {(showAddPopup || showEditPopup) && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="popup-content"
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '5px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3>{editId ? 'Edit Instrumen' : 'Tambah Instrumen Baru'}</h3>
              <button 
                onClick={handleClosePopup}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <input name="nama" placeholder="Nama Instrumen" value={form.nama} onChange={handleChange} required className="form-input" />
                <input name="kode" placeholder="Kode Unik" value={form.kode} onChange={handleChange} required className="form-input" />
              </div>
              <div className="form-row">
                <input name="spesifikasi" placeholder="Spesifikasi" value={form.spesifikasi} onChange={handleChange} className="form-input" />
                <input name="lokasi" placeholder="Lokasi Penyimpanan" value={form.lokasi} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-row file-inputs">
                <label className="file-label">
                  Gambar (Max 1MB):
                  <input type="file" accept="image/*" onChange={handleGambarChange} className="file-input" />
                  {form.gambarBase64 && <span className="file-name">Gambar Terpilih</span>}
                </label>
                <label className="file-label">
                  SOP (PDF/DOC, Max 1MB):
                  <input type="file" accept="application/pdf,application/msword,.doc,.docx" onChange={handleSOPChange} className="file-input" />
                  {form.sopBase64 && <span className="file-name">{form.sopFileName || 'SOP Terpilih'}</span>}
                </label>
              </div>
              {editId && (
                <div className="current-files">
                  {form.gambarBase64 && (
                    <div className="current-file-item">
                      <span>Gambar Saat Ini: </span>
                      <img src={form.gambarBase64} alt="Current Instrumen" style={{ maxWidth: '100px', maxHeight: '60px', verticalAlign: 'middle' }} />
                    </div>
                  )}
                  {form.sopBase64 && (
                    <div className="current-file-item">
                      <span>SOP Saat Ini: </span>
                      <span>{form.sopFileName || 'File SOP Tersedia'}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="form-actions" style={{ marginTop: '15px' }}>
                <button type="submit" disabled={loading} className="btn btn-primary">
                  {loading ? 'Menyimpan...' : (editId ? 'Update Instrumen' : 'Tambah Instrumen')}
                </button>
                <button
                  type="button"
                  onClick={handleClosePopup}
                  disabled={loading}
                  className="btn btn-secondary"
                  style={{ marginLeft: '10px' }}
                >
                  Batal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table Section */}
      <div className="instrumen-table-container">
        <table className="instrumen-table">
          <thead>
            <tr>
              <th>Nama</th>
              <th>Kode</th>
              <th>Spesifikasi</th>
              <th>Lokasi</th>
              <th>Gambar</th>
              <th>SOP</th>
              <th>QR Code</th>
              <th>Aksi</th>
          </tr>
        </thead>
        <tbody>
          {/* Use filtered list */}
          {filteredInstrumen.map(item => (
            <tr key={item.id}>
              <td>{item.nama}</td>
              <td>{item.kode}</td>
              <td>{item.spesifikasi}</td>
              <td>{item.lokasi}</td>
              <td>
                {item.gambarBase64 ? (
                  <img 
                    src={item.gambarBase64} 
                    alt={item.nama} 
                    className="table-image" 
                    onClick={() => handleImageClick(item.gambarBase64)}
                  />
                ) : 'N/A'}
              </td>
              <td>
                {/* Replace text with a button */}
                {(item.sopBase64 || item.sopUrl) ? (
                  <button
                    onClick={() => handleBacaSOP(item)}
                    className="btn btn-sop"
                    style={{
                      padding: '5px 25px',
                      fontSize: '12px',
                      backgroundColor: '#ffc107',
                      color: '#212529',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginRight: '5px',
                      display: 'flex',          // Tambah ini
                      alignItems: 'center',     // Vertikal center
                      justifyContent: 'center'  // Horizontal center
                    }}
                    disabled={loading}
                  >
                    Baca SOP
                  </button>
                ) : (
                  'N/A'
                )}
              </td>
              <td className="qr-code-cell">
                {item.id && (
                  <>
                    <div 
                      onClick={() => handleImageClick(`data:image/svg+xml;utf8,${encodeURIComponent(new XMLSerializer().serializeToString(document.getElementById(`qr-${item.id}`)))}`)}
                      style={{ cursor: 'pointer', display: 'inline-block' }}
                    >
                      <QRCodeSVG id={`qr-${item.id}`} value={item.id} size={64} includeMargin={false} />
                    </div>
                    <button
                      onClick={() => handleDownloadQR(item.id, item.nama)}
                      className="btn btn-download-qr"
                      title="Download QR Code"
                      disabled={loading}
                    >
                      ⬇️
                    </button>
                  </>
                )}
              </td>
              {/* Updated Action Buttons */}
              <td className="action-buttons">
                <button onClick={() => handleEditClick(item)} className="btn btn-edit" disabled={loading}>Edit</button>
                <button onClick={() => handleDelete(item.id)} className="btn btn-delete" disabled={loading}>Hapus</button>
              </td>
            </tr>
          ))}
          {/* Update empty state message */}
          {filteredInstrumen.length === 0 && !loading && (
            <tr>
              <td colSpan="8" style={{ textAlign: 'center' }}>
                 {searchTerm ? 'Tidak ada instrumen yang cocok dengan pencarian.' : 'Belum ada data instrumen.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>

    {/* Image Popup Modal */}
    {selectedImage && (
      <div 
        className="image-popup-overlay" 
        onClick={handleCloseImage}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}
      >
        <div 
          className="image-popup-content"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'relative',
            maxWidth: '90%',
            maxHeight: '90%',
            backgroundColor: 'white',
            padding: '10px',
            borderRadius: '5px'
          }}
        >
          <button 
            onClick={handleCloseImage}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            &times;
          </button>
          <img 
            src={selectedImage} 
            alt="Enlarged view" 
            style={{
              maxWidth: '100%',
              maxHeight: '80vh',
              display: 'block'
            }}
          />
        </div>
      </div>
    )}
  </div>
);
};

export default ManajemenInstrumen;
